// Shift close: pure calculations and validation (ported from idokon-desktop)
import { formatMoney } from 'helpers/helpers'

export const CURRENCY_UZS = 'UZS'
export const CURRENCY_USD = 'USD'

// payment types of the 1st modal; Click / Payme / Uzum / bank card are all inside terminal
export const PAYMENT_ROWS = [
	{ key: 'cashUZS', i18n: 'SC_CASH_UZS', currency: CURRENCY_UZS, cash: true },
	{ key: 'cashUSD', i18n: 'SC_CASH_USD', currency: CURRENCY_USD, cash: true },
	{ key: 'terminal', i18n: 'SC_TERMINAL', currency: CURRENCY_UZS, cash: false },
]

// only cash can be left in the cashbox, terminal fully goes to the safe
export const CASH_ROWS = PAYMENT_ROWS.filter(row => row.cash)

// backend ids (see cheques: payment type 1 = cash, 2 = terminal; currency 1 = sum, 2 = USD)
const PAYMENT_TYPE_CASH = 1

// currency ids differ between environments, so the name is the main sign (as in CashRegistersTab)
function isUsd(item) {
	return /usd|\$|доллар|dollar/i.test(item.currencyName || '')
}

// item with backend currencyName -> CURRENCY_UZS / CURRENCY_USD
export function currencyOf(item) {
	return isUsd(item) ? CURRENCY_USD : CURRENCY_UZS
}

// ShiftCloseDetailsDTO -> list of ShiftBalanceDetailsDTO (the response itself or its first array field)
function getBalanceList(details) {
	if (Array.isArray(details)) return details
	return Object.values(details || {}).find(Array.isArray) || []
}

// GET /cash-shift/{id}/closing-details -> expected / actual amounts of the 1st modal;
// everything that is not cash (Click / Payme / Uzum / bank card) is summed into terminal
export function parseShiftCloseDetails(details) {
	const expected = { cashUZS: 0, cashUSD: 0, terminal: 0 }
	// null = backend sent no actualAmount, the input stays empty
	const actual = { cashUZS: null, cashUSD: null, terminal: null }
	// row key -> backend currencyId / paymentTypeId, needed for the actual amounts and cash transfer requests
	const currencyIds = {}
	const paymentTypeIds = {}
	const terminalBreakdown = []

	getBalanceList(details).forEach(item => {
		const isCash = Number(item.paymentTypeId) === PAYMENT_TYPE_CASH
		const key = isCash ? (isUsd(item) ? 'cashUSD' : 'cashUZS') : 'terminal'

		expected[key] += toAmount(item.expectedAmount)
		if (item.actualAmount !== null && item.actualAmount !== undefined) {
			actual[key] = toAmount(actual[key]) + toAmount(item.actualAmount)
		}

		currencyIds[key] = item.currencyId
		paymentTypeIds[key] = item.paymentTypeId
		if (!isCash && item.paymentTypeName && !terminalBreakdown.includes(item.paymentTypeName)) {
			terminalBreakdown.push(item.paymentTypeName)
		}
	})

	return { expected, actual, currencyIds, paymentTypeIds, terminalBreakdown }
}

// actual amounts -> initial values of the fact inputs
export function actualToFact(actual) {
	const fact = {}
	PAYMENT_ROWS.forEach(row => {
		const value = actual?.[row.key]
		fact[row.key] = value === null || value === undefined ? '' : String(roundAmount(value, row.currency))
	})
	return fact
}

// NaN / Infinity / "" / null -> 0
export function toAmount(value) {
	const number = Number(value)
	return Number.isFinite(number) ? number : 0
}

// keeps digits and a single dot
export function sanitizeAmountInput(value) {
	let cleaned = String(value ?? '').replace(/[^0-9.]/g, '')
	const firstDot = cleaned.indexOf('.')
	if (firstDot !== -1) {
		cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
	}
	if (cleaned.charAt(0) === '.') {
		cleaned = '0' + cleaned
	}
	return cleaned
}

// "1500000.5" -> "1 500 000.5" (only the integer part is grouped)
export function formatAmountInput(value) {
	if (!value) return ''
	const [whole, fraction] = String(value).split('.')
	const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
	return fraction !== undefined ? `${grouped}.${fraction}` : grouped
}

// USD with cents, UZS rounded to integer
export function roundAmount(value, currency) {
	const number = toAmount(value)
	return currency === CURRENCY_USD ? Math.round(number * 100) / 100 : Math.round(number)
}

// empty input differs from "0": the cashier must type 0 consciously
export function isEmptyAmount(value) {
	return value === null || value === undefined || String(value).trim() === '' || String(value).trim() === '.'
}

// "1 500 000 Сум" / "$300" / "-$50"
export function formatAmountWithCurrency(value, currency, sumLabel) {
	const number = roundAmount(value, currency)
	const sign = number < 0 ? '-' : ''
	const absolute = Math.abs(number)

	if (currency === CURRENCY_USD) {
		return `${sign}$${formatMoney(absolute, Number.isInteger(absolute) ? 0 : 2)}`
	}
	return `${sign}${formatMoney(absolute, 0)} ${sumLabel}`
}

export const CURRENCY_SIGNS = { [CURRENCY_UZS]: 'UZS', [CURRENCY_USD]: '$' }

// "10 000 UZS" / "25 $" / "-500 UZS"
export function formatSum(value, currency) {
	const number = roundAmount(value, currency)
	const absolute = Math.abs(number)
	const decimals = currency === CURRENCY_USD && !Number.isInteger(absolute) ? 2 : 0
	return `${number < 0 ? '-' : ''}${formatMoney(absolute, decimals)} ${CURRENCY_SIGNS[currency]}`
}

// difference = fact - expected
export function calcDifference(fact, expected, currency) {
	return roundAmount(toAmount(fact) - toAmount(expected), currency)
}

export function differenceState(difference) {
	if (difference < 0) return 'shortage'
	if (difference > 0) return 'surplus'
	return 'zero'
}

// cash to safe = fact cash - cash left in the cashbox
export function calcToSafe(fact, leave, currency) {
	return roundAmount(toAmount(fact) - toAmount(leave), currency)
}

// epsilon because of float errors in USD cents
export function exceedsAvailableCash(leave, fact) {
	return toAmount(leave) - toAmount(fact) > 0.005
}

export function validateFactStep(fact) {
	const errors = {}
	PAYMENT_ROWS.forEach(row => {
		if (isEmptyAmount(fact[row.key])) {
			errors[row.key] = 'SC_ERROR_FACT_REQUIRED'
		}
	})
	return errors
}

export function validateLeaveStep(leave, fact) {
	const errors = {}
	CASH_ROWS.forEach(row => {
		if (exceedsAvailableCash(leave[row.key], fact[row.key])) {
			errors[row.key] = 'SC_ERROR_LEAVE_EXCEEDS_CASH'
		}
	})
	return errors
}

export function hasErrors(errors) {
	return Object.keys(errors).length > 0
}

// PATCH /cash-shift/{id}/actual-amounts items: fact amount of every row the backend knows
export function buildActualAmountsPayload(fact, currencyIds, paymentTypeIds) {
	return PAYMENT_ROWS
		.filter(row => currencyIds[row.key] !== undefined && paymentTypeIds[row.key] !== undefined)
		.map(row => ({
			paymentTypeId: paymentTypeIds[row.key],
			currencyId: currencyIds[row.key],
			actualAmount: roundAmount(fact[row.key], row.currency),
		}))
}

// POST /shift-close/{id}/cash-transfer body: cash left in the cashbox per currency, the rest goes to the safe
export function buildCashTransferPayload(leave, currencyIds) {
	return CASH_ROWS
		.filter(row => currencyIds[row.key] !== undefined)
		.map(row => ({
			currencyId: currencyIds[row.key],
			remainingInCashbox: roundAmount(leave[row.key], row.currency),
		}))
}
